import { EditOutlined } from "@ant-design/icons";
import { toAssessmentPath } from "@p0security/shared/assessment/helper";
import { AggregatedNode } from "@p0security/shared/graph/aggregate";
import { Node } from "@p0security/shared/graph/types";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  ALL_SCOPE_SENTINEL,
  type ItemAssessmentScope,
  toKey,
} from "@p0security/shared/types/assessment";
import {
  AnyAggregates,
  AnyNode,
  AssessmentNodes,
  TargetNodeType,
} from "@p0security/shared/types/assessment/data/index";
import {
  Finding,
  FindingState,
} from "@p0security/shared/types/assessment/finding";
import {
  Monitor,
  MonitorScope,
} from "@p0security/shared/types/assessment/monitor";
import { Alert, Button, Flex, Grid, Spin, Typography } from "antd";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { Tenant } from "components/Login";
import { NormalizedUser } from "components/Login/types/auth";
import { ResizableTableColumnsType } from "components/ResizableTable";
import { VerticalSpacedDiv } from "components/divs";
import { useFlags } from "launchdarkly-react-client-sdk";
import { compact, maxBy } from "lodash";
import pluralize from "pluralize";
import { FirestoreDoc, useFirestoreDoc } from "providers/FirestoreProvider";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import styled from "styled-components";
import {
  QueryParamProvider,
  StringParam,
  useQueryParam,
} from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

import { Heading } from "../../Heading";
import { LegacyButtonGroup } from "../../common/LegacyButtonGroup";
import { AssessmentGraph } from "../components/AssessmentGraph";
import { EditMonitorModal } from "../components/EditMonitorModal";
import { FindingsAssignModal } from "../components/FindingsAssignModal";
import { FindingsDatePicker } from "../components/FindingsDatePicker";
import { FindingsFixOrRevertModal } from "../components/FindingsFixOrRevertModal";
import { FindingsIgnoreModal } from "../components/FindingsIgnoreModal";
import { FindingsManageModal } from "../components/FindingsManageModal";
import { FindingsSelect } from "../components/FindingsSelect";
import { ScopeScoring, ScopeSelect } from "../components/ScopeSelect";
import { AssignmentColumn } from "../components/cells/AssignmentColumn";
import { DetailsColumn } from "../components/cells/DetailsColumn";
import { SinceColumn } from "../components/cells/SinceColumn";
import { MonitorPriority } from "../components/monitor/MonitorSeverity";
import { PriorityTooltip } from "../components/monitor/PriorityTooltip";
import { AggregatedGraphContext } from "../contexts/AggregatedGraphContext";
import {
  FindingsContext,
  MAX_FINDINGS,
  MonitorWithFindings,
} from "../contexts/FindingsContext";
import { ScopeContext } from "../contexts/ScopeContext";
import { useControls } from "../hooks/useControls";
import { useTracker } from "../hooks/useTracker";
import { monitorFindingsToNodes } from "../utils/monitor";
import { FindingDetail } from "./FindingDetail";

export type MonitorActionProps = {
  actOn: FirestoreDoc<Finding>[];
  allNodes: AnyNode[] | undefined;
  count: number;
  findingFor: (node: AnyNode) => FirestoreDoc<Finding> | undefined;
  integration: MonitorScope;
  monitor: MonitorWithFindings;
  selectedNodes: AnyNode[] | undefined;
  state: FindingState;
  user?: NormalizedUser;
};

export const MonitorResults: React.FC = () => {
  return (
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      <MonitorResultsContent />
    </QueryParamProvider>
  );
};

const hasFixModal = (
  integration: MonitorScope,
  monitor: Monitor | undefined,
  state: FindingState
) =>
  !!integration &&
  !!monitor &&
  !!(
    (monitor.fix?.[integration] && state === "open") ||
    (monitor.revert?.[integration] && state === "resolved")
  );

const hasManageModal = (
  integration: MonitorScope,
  monitor: Monitor | undefined
) => {
  return !!integration && !!monitor && monitor.management;
};
const hasIgnoreToggle = (state: FindingState) =>
  state === "open" || state === "ignored";

const hasAssignModal = (
  state: FindingState,
  findings: FirestoreDoc<Finding>[]
) => state === "open" && findings.find((f) => !f.data.issue);

const ActionBar: React.FC<Omit<MonitorActionProps, "actOn" | "count">> = (
  props
) => {
  const { assessmentManage: hasManagement } = useFlags();
  const tracker = useTracker();
  const hasAssignment = !!tracker;
  const { count, actOn } = useMemo(() => {
    const count = props.selectedNodes?.length ?? 0;
    const nodes = count ? props.selectedNodes : props.allNodes;
    const actOn = compact((nodes ?? []).map(props.findingFor));
    return { count, actOn };
  }, [props]);

  const innerProps = useMemo(
    () => ({ ...props, actOn, count }),
    [props, actOn, count]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "baseline",
      }}
    >
      <LegacyButtonGroup>
        {hasAssignment && hasAssignModal(props.state, actOn) && (
          <FindingsAssignModal {...innerProps} />
        )}
        {hasIgnoreToggle(props.state) && (
          <FindingsIgnoreModal {...innerProps} />
        )}
        {hasFixModal(props.integration, props.monitor, props.state) && (
          <FindingsFixOrRevertModal {...innerProps} />
        )}
        {hasManagement && hasManageModal(props.integration, props.monitor) && (
          <FindingsManageModal {...innerProps} />
        )}
      </LegacyButtonGroup>
      <div
        style={{
          borderTopRightRadius: "2px",
          borderBottomRightRadius: "2px",
          minWidth: "6.5em",
          maxWidth: "10em",
          padding: "5px",
          backgroundColor: "#f5f5f5",
        }}
      >
        {count || "All"}
        &nbsp;{pluralize("findings", count)}
      </div>
    </div>
  );
};

// Styled components
const FilterControlsContainer = styled.div<{ flexWrap?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: ${(props) => (props.flexWrap ? "nowrap" : "wrap")};
`;

const MonitorResultsContent: React.FC = () => {
  const { monitorId, findingId, orgSlug } = useParams();
  const [nodeKey, setNodeKey] = useQueryParam("node", StringParam);

  const { current, details, runAssessmentNow, last } = useContext(
    SelectedEnvironmentContext
  );
  const assessmentId = details?.assessmentId;
  const tenantId = useContext(Tenant);
  const {
    isValidMonitorScope,
    selectedMonitor: monitor,
    state: findingsState,
  } = useContext(FindingsContext);
  const { aggregated } = useContext(AggregatedGraphContext);
  const { integration, scopeKey, setScopeKey, validScopeKeys } =
    useContext(ScopeContext);
  const { controls, setControls } = useControls();
  const [searchedNodes, setSearchedNodes] = useState<AnyNode[]>();
  const [selectedNodes, setSelectedNodes] = useState<
    AggregatedNode<AssessmentNodes, TargetNodeType, AnyAggregates>[]
  >([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const closeEditModal = useCallback(() => setEditModalOpen(false), []);
  const openEditModal = useCallback(() => setEditModalOpen(true), []);

  // Clear selected nodes on scope change
  useEffect(() => {
    setSelectedNodes([]);
  }, [scopeKey]);

  const { xl } = Grid.useBreakpoint();

  const monitorControls = useMemo(
    () => ({
      where: monitor?.searchTerm ?? "",
      show: monitor?.show ?? "grant",
    }),
    [monitor]
  );

  const inferredControls = useMemo(() => {
    return { ...controls, show: monitorControls.show };
  }, [controls, monitorControls]);

  const findings = useMemo(
    () =>
      // This component and FindingsContext both rerender when scopeKey changes.
      // FindingsContext will reload the findings based on the latest scopeKey, so
      // when this component switches from all scopes to a single scope, there will
      // be a brief moment when FindingsContext is still returning the All scope findings.
      // To guard against this, we set findings to undefined if the current listed findings contain
      // a scope other than the current scopeKey.
      (scopeKey !== ALL_SCOPE_SENTINEL &&
        monitor?.scopedFindings.some(
          (finding) => finding.data.scopeKey !== scopeKey
        )) ||
      // When this component first loads, the "dirty hack" below checks to see if the selected scope is
      // all scopes, but all scopes doesn't apply to the current monitor.  If that's the case, this
      // component will switch the scope to the highest scoring scope.  In anticipation of this, we
      // prevent loading the findings until the scope has been toggled
      (scopeKey === ALL_SCOPE_SENTINEL &&
        !monitor?.scopes?.includes(ALL_SCOPE_SENTINEL))
        ? undefined
        : monitor?.scopedFindings,
    [monitor, scopeKey]
  );

  const filtered = useMemo(() => {
    if (!monitorId || !aggregated) return undefined;
    if (!findings) return { nodes: [] };
    return monitorFindingsToNodes(
      findings.map((f) => f.data),
      aggregated
    );
  }, [aggregated, findings, monitorId]);

  const openedFindingsNode = useMemo(() => {
    if (!nodeKey) return undefined;
    return searchedNodes?.find((n) => n.key === nodeKey);
  }, [nodeKey, searchedNodes]);

  // TODO: Improve performance if this ever matters
  const findingFor = useCallback(
    (node: Node<any, any>) =>
      findings?.find(
        (d) => d.data.node.key === node.key && d.data.node.type === node.type
      ),
    [findings]
  );

  const extraColumns = useMemo(():
    | ResizableTableColumnsType<AnyNode>
    | undefined => {
    if (!assessmentId || !orgSlug || !monitorId || !findings) return undefined;

    return [
      DetailsColumn((node) => {
        const linkId = findingFor(node)?.id;

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set("node", node.key);

        return linkId
          ? {
              disabled: linkId === findingId,
              key: linkId,
              to: `/o/${orgSlug}/${
                AppPaths.Posture
              }/monitors/${monitorId}/findings/${linkId}?${searchParams.toString()}`,
            }
          : undefined;
      }),
      SinceColumn(findingsState, (node) => findingFor(node)?.data.trigger.at),
      AssignmentColumn((node) => findingFor(node)?.data.issue),
    ];
  }, [
    assessmentId,
    findingFor,
    findingId,
    findings,
    findingsState,
    monitorId,
    orgSlug,
  ]);

  // Clean up node param when drawer closes
  useEffect(() => {
    if (!findingId && nodeKey) {
      setNodeKey(undefined);
    }
  }, [findingId, nodeKey, setNodeKey]);

  // Is undefined unless user has navigated to a findings page
  const findingDoc = useFirestoreDoc<Finding>(
    findingId && assessmentId
      ? `${toAssessmentPath(tenantId, assessmentId)}/findings/${findingId}`
      : undefined,
    { live: true }
  );

  const unevaluatedMonitorText = useMemo(() => {
    if (monitor) {
      if (monitor.scopedFindings?.length === 0) {
        return `This monitor has not been evaluated yet. Results for this monitor will be available after the next environment scan.`;
      } else {
        return `This monitor's search term has been updated, and the results on the page are out of date. Updated results for this monitor will be available after the next scan.`;
      }
    }
    return "";
  }, [monitor]);

  const hasBulkSelect = useMemo(
    () =>
      hasIgnoreToggle(findingsState) ||
      hasFixModal(integration, monitor, findingsState),
    [findingsState, integration, monitor]
  );

  const frozen = useMemo(
    () => ({
      show: monitorControls.show,
      // If a finding is resolved, then the monitor search will no longer match the finding node.
      // Ensure resolved findings are actually displayed by removing the frozen where. Note that this
      // means that the graph display will not show any attack path.
      terms: findingsState === "resolved" ? "" : monitorControls.where,
    }),
    [findingsState, monitorControls]
  );
  // Rank scopes by number of applicable findings
  const scopeScoring: ScopeScoring = useMemo(() => {
    if (!monitor) {
      return () => ({ value: 0, label: "0" });
    }
    return (scope: ItemAssessmentScope) => {
      const k = toKey(scope);
      const count = monitor.countsByScope[k] ?? 0;
      return { value: count, label: String(count) };
    };
  }, [monitor]);

  // If no scope is selected, or the "all" scope is selected, but this UI does not support
  // the "all" scope, redirect to the most highly scored scope, or the first scope if there
  // is no scoring.
  // nosemgrep use-useguardedeffect
  useEffect(() => {
    // Do not change selected scope if it is currently valid
    if (!last.doc?.data.scope?.length || isValidMonitorScope) return;
    const next = maxBy(
      last.doc.data.scope.filter((s) =>
        monitor?.scopes.includes(s.integration)
      ),
      (s) => scopeScoring(s).value
    );

    // If the monitor does not include the next scope, then do not update the scope
    if (next) {
      setScopeKey(toKey(next), "replaceIn");
    }
  }, [
    last.doc,
    scopeKey,
    validScopeKeys,
    scopeScoring,
    monitor?.scopes,
    setScopeKey,
    isValidMonitorScope,
  ]);

  const scopeNode = useMemo(
    () =>
      monitor ? (
        <ScopeSelect
          includeAll={true}
          scoring={scopeScoring}
          scopesToInclude={monitor.scopes}
          wide
        />
      ) : null,
    [monitor, scopeScoring]
  );

  const settingsDisables = useMemo(
    () => ({ stopOn: !(scopeKey === ALL_SCOPE_SENTINEL) }),
    [scopeKey]
  );

  return monitorId && monitor ? (
    <>
      <EditMonitorModal
        editModalOpen={editModalOpen}
        closeEditModal={closeEditModal}
        modalMonitor={monitor}
        isCustom={monitor.isCustom ?? false}
      />
      <VerticalSpacedDiv style={{ maxWidth: "1200px" }}>
        <div>
          <Heading
            title={
              <>
                {monitor.label}{" "}
                <Button type="text" onClick={openEditModal}>
                  <EditOutlined
                    style={{
                      fontSize: "20px",
                      verticalAlign: "middle",
                    }}
                  />
                </Button>
              </>
            }
          />
          <Flex align="baseline" gap="small">
            <div>
              <MonitorPriority monitor={monitor} />
              &nbsp;
              <PriorityTooltip />
            </div>
            {monitor.description && (
              <Typography.Paragraph>{monitor.description}</Typography.Paragraph>
            )}
          </Flex>
        </div>
        {monitor.hasBeenEvaluated === false && (
          <Alert
            type="warning"
            description={unevaluatedMonitorText}
            message="One more thing..."
            action={
              current.isInProgress ? (
                <Typography.Text type="secondary">
                  Scanning environment
                </Typography.Text>
              ) : (
                <Button
                  type="default"
                  disabled={!current.isCompleted}
                  onClick={runAssessmentNow}
                >
                  Scan environment now
                </Button>
              )
            }
            style={{
              marginBottom: 16,
              color: "#614700",
              alignItems: "center",
            }}
          />
        )}
        <FilterControlsContainer flexWrap={xl}>
          <FindingsSelect />
          <FindingsDatePicker />
          {hasBulkSelect && (
            <ActionBar
              allNodes={searchedNodes}
              findingFor={findingFor}
              integration={integration}
              monitor={monitor}
              selectedNodes={selectedNodes}
              state={findingsState}
            />
          )}
        </FilterControlsContainer>

        {(monitor.countsByScope[scopeKey] ?? 0) > MAX_FINDINGS && (
          <Alert
            type="warning"
            description={
              MAX_FINDINGS > monitor.scopedFindings.length
                ? `Showing the filtered findings from the first ${MAX_FINDINGS} findings.`
                : `Too many findings. Showing only the first ${monitor.scopedFindings.length}.`
            }
          />
        )}
        <AssessmentGraph
          aggregated={filtered}
          controls={inferredControls}
          onControls={setControls}
          onSearch={setSearchedNodes}
          scopeNode={scopeNode}
          selectedNodes={selectedNodes}
          onSelection={setSelectedNodes}
          settingsDisables={settingsDisables}
          frozen={frozen}
          extraColumns={extraColumns}
          isMonitorDisplay={true}
        />
      </VerticalSpacedDiv>
      {findingId && (
        <FindingDetail node={openedFindingsNode} finding={findingDoc} />
      )}
    </>
  ) : (
    <Spin />
  );
};
